import {createContext, useContext, useReducer, useMemo, ReactNode, Dispatch} from "react";

// The Fai main context
const Fai = createContext<[State, Dispatch<Action>] | null>(null);
// Setting custom name for the context which is visible on react dev tools
Fai.displayName = "FaiContext";

interface State {
    miniSidenav: boolean;
    darkSidenav: boolean;
    sidenavColor: string | null;
    transparentNavbar: boolean;
    fixedNavbar: boolean;
    openConfigurator: boolean;
    direction: string;
    layout: string;
    darkMode: boolean;
}

interface Action {
    type: string;
    value: any;
}

// Fai reducer
function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "MINI_SIDENAV": {
            return {...state, miniSidenav: action.value};
        }
        case "DARK_SIDENAV": {
            return {...state, darkSidenav: action.value};
        }
        case "SIDENAV_COLOR": {
            return {...state, sidenavColor: action.value};
        }
        case "TRANSPARENT_NAVBAR": {
            return {...state, transparentNavbar: action.value};
        }
        case "FIXED_NAVBAR": {
            return {...state, fixedNavbar: action.value};
        }
        case "OPEN_CONFIGURATOR": {
            return {...state, openConfigurator: action.value};
        }
        case "DIRECTION": {
            return {...state, direction: action.value};
        }
        case "LAYOUT": {
            return {...state, layout: action.value};
        }
        case "DARK_MODE": {
            return {...state, darkMode: action.value};
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}

interface FaiControllerProviderProps {
    children: ReactNode;
}

// Fai context provider
function FaiControllerProvider({children}: FaiControllerProviderProps) {
    const initialState: State = {
        miniSidenav: true,
        darkSidenav: true,
        sidenavColor: 'white',
        transparentNavbar: false,
        fixedNavbar: false,
        openConfigurator: false,
        direction: "ltr",
        layout: "dashboard",
        darkMode: false,
    };

    const [controller, dispatch] = useReducer(reducer, initialState);

    const value = useMemo(() => [controller, dispatch] as [State, Dispatch<Action>], [controller, dispatch]);
    return <Fai.Provider value={value}>{children}</Fai.Provider>;
}

// custom hook for using context
function useFaiController() {
    const context = useContext(Fai);

    if (!context) {
        throw new Error("useFaiController should be used inside the FaiControllerProvider.");
    }

    return context;
}

// Context module functions
const setMiniSidenav = (dispatch: (action: Action) => void, value: boolean) => dispatch({type: "MINI_SIDENAV", value});
const setDarkSidenav = (dispatch: (action: Action) => void, value: boolean) => dispatch({type: "DARK_SIDENAV", value});
const setSidenavColor = (dispatch: (action: Action) => void, value: string | null) => dispatch({
    type: "SIDENAV_COLOR",
    value
});
const setTransparentNavbar = (dispatch: (action: Action) => void, value: boolean) => dispatch({
    type: "TRANSPARENT_NAVBAR",
    value
});
const setFixedNavbar = (dispatch: (action: Action) => void, value: boolean) => dispatch({type: "FIXED_NAVBAR", value});
const setOpenConfigurator = (dispatch: (action: Action) => void, value: boolean) => dispatch({
    type: "OPEN_CONFIGURATOR",
    value
});
const setDirection = (dispatch: (action: Action) => void, value: string) => dispatch({type: "DIRECTION", value});
const setLayout = (dispatch: (action: Action) => void, value: string) => dispatch({type: "LAYOUT", value});
const setDarkMode = (dispatch: (action: Action) => void, value: boolean) => dispatch({type: "DARK_MODE", value});

export {
    FaiControllerProvider,
    useFaiController,
    setMiniSidenav,
    setDarkSidenav,
    setSidenavColor,
    setTransparentNavbar,
    setFixedNavbar,
    setOpenConfigurator,
    setDirection,
    setLayout,
    setDarkMode,
};
